import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import { KRATOS_BASE_PATH } from '@/libs/kratos'
import router          from '@/router'
import { useAppStore } from '@/stores/app'

export interface IKratosRawResponse {
    status: number
    data:   unknown
}

export interface IKratosRequest {
    method?: 'GET' | 'POST'
    body?:   Record<string, unknown>
}

/**
 * Header names as a canonically-cased plain record. @vueuse/core spreads its own `Content-Type`
 * and whatever `beforeFetch` returns; a `Headers` instance arrives lower-cased and both keys
 * survive, so the request goes out as `Content-Type: application/json, application/json` —
 * which Kratos refuses before it reads the flow (found in the reference implementation).
 */
const canonicalHeaderName = (name: string): string =>
    name.toLowerCase().replace(/(^|-)([a-z])/g, (_match, boundary: string, letter: string) => `${boundary}${letter.toUpperCase()}`)

const headerRecord = (headers: HeadersInit | undefined): Record<string, string> => {
    const entries = headers instanceof Headers
        ? [...headers.entries()]
        : Array.isArray(headers)
            ? headers
            : Object.entries(headers ?? {})

    return Object.fromEntries(entries.map(([name, value]) => [canonicalHeaderName(name), String(value)]))
}

const jsonHeaders = ({ options }: { options: RequestInit }) => {
    const headers = headerRecord(options.headers)
    headers.Accept = 'application/json'
    options.headers = headers
    return { options }
}

/**
 * The single HTTP entry point. Two `createFetch` instances, both same-origin and cookie-bearing:
 * `fetch` for auth-api (`/api/auth/v1`) and `kratosFetch` for the Kratos public API (`/kratos`,
 * prefix stripped by the edge). No `Authorization` header: the session is Kratos's HttpOnly
 * cookie, which the browser attaches itself.
 */
export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    // A 401 sends the person to sign-in once per page load, as in iam-web: when auth-api and
    // Kratos disagree about the session, /login and /settings must not bounce off each other.
    let redirecting = false

    const fetch = createFetch({
        baseUrl:      '/api/auth/v1',
        fetchOptions: { credentials: 'include' },
        options:      {
            updateDataOnError: true,
            beforeFetch:       jsonHeaders,
            async onFetchError(ctx) {
                if (ctx.response?.status === 401 && !redirecting) {
                    redirecting = true
                    appStore.setLogout()
                    await router.push({ name: 'login' })
                }
                return ctx
            },
        },
    })

    // No `onFetchError`: a Kratos 401/403/410/422 is flow data that `libs/kratos.ts` classifies.
    const kratosFetch = createFetch({
        baseUrl:      KRATOS_BASE_PATH,
        fetchOptions: { credentials: 'include' },
        options:      {
            updateDataOnError: true,
            beforeFetch:       jsonHeaders,
        },
    })

    /** Every Kratos request goes through here: status + parsed body, nothing thrown. */
    const kratosRequest = async (path: string, init: IKratosRequest = {}): Promise<IKratosRawResponse> => {
        const request = init.method === 'POST'
            ? kratosFetch(path).post(init.body).json<unknown>()
            : kratosFetch(path).get().json<unknown>()

        const { statusCode, response, data } = await request

        return {
            status: statusCode.value ?? response.value?.status ?? 0,
            data:   data.value,
        }
    }

    return { fetch, kratosRequest }
})
